import type { ReactNode, KeyboardEventHandler, ForwardedRef } from "react";
import { forwardRef } from "react";
import { Button, VisuallyHidden } from "reakit";
import { pxToRem } from "@mos-cat/ds-theme";
import type { BoxProps } from "../../Box/Box";
import { Box } from "../../Box/Box";

import { combineStyles } from "../helpers";
import { WHCM, WHCMstyles } from "../../constants";

type Props = {
  children: ReactNode;
  selected: boolean;
  isInInterval: boolean;
  disabled: boolean;
  "data-date": string;
  onKeyDown: KeyboardEventHandler;
  onClick: () => void;
} & BoxProps;

/**
 * @beta
 */
export const DateButton = forwardRef(function DateButtonComponent(
  {
    children,
    selected,
    isInInterval,
    disabled,
    onKeyDown,
    onClick,
    styles,
    tabIndex,
    ...props
  }: Props,
  ref: ForwardedRef<HTMLButtonElement>
): JSX.Element {
  const combinedStyles = combineStyles(
    {
      fontSize: "fs-14",
      lineHeight: "calendarBtnLineHeight",
      ...styles,
      position: "relative",
      backgroundColor: "calendarBtnBg",
      color: "calendarBtnText",
      ":focus": {
        outline: "none",
        outlineColor: "focus",
        outlineStyle: "solid",
        outlineWidth: pxToRem(3),
        zIndex: 2,
      },
    },
    [
      selected,
      {
        backgroundColor: "calendarBtnSelectedBg",
        color: "calendarSelectedText",

        ":hover": {
          backgroundColor: "calendarBtnSelectedHoverBg",
        },

        ":active": {
          backgroundColor: "calendarBtnSelectedActiveBg",
        },

        [WHCM]: {
          ...WHCMstyles,
          backgroundColor: "WindowText",
          color: "Window",

          ":hover": {
            backgroundColor: "WindowText",
            color: "Window",
          },

          ":focus": {
            outlineColor: "auto",
          },
        },
      },
    ],
    [
      disabled,
      {
        backgroundColor: "calendarBtnDisabledBg",
        color: "calendarBtnDisabledText",

        [WHCM]: {
          backgroundColor: "Window",
          borderColor: "GrayText",
          color: "GrayText",
        },
      },
    ],
    [
      isInInterval,
      {
        backgroundColor: "calendarIntervalBg",

        ":hover": {
          backgroundColor: "calendarIntervalHoverBg",
        },
        ":active": {
          backgroundColor: "calendarIntervalActiveBg",
        },

        [WHCM]: {
          "&, &:hover, &:active": {
            ...WHCMstyles,
            backgroundColor: "WindowText",
            color: "Window",
          },
        },
      },
    ],
    [
      !(selected || disabled || isInInterval),
      {
        ":hover": {
          backgroundColor: "calendarBtnHoverBg",
        },
        ":active": {
          backgroundColor: "calendarBtnActiveBg",
        },
      },
    ]
  );
  return (
    <Box
      ref={ref}
      as={Button}
      type="button"
      aria-selected={selected}
      aria-disabled={disabled}
      tabIndex={tabIndex}
      onClick={onClick}
      onKeyDown={onKeyDown}
      size={1}
      styles={combinedStyles}
      {...props}
    >
      {children}
      {selected && <VisuallyHidden>выбрано</VisuallyHidden>}
    </Box>
  );
});
